import MenuRoundedIcon from "@mui/icons-material/MenuRounded";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useAuth } from "../../context/AuthContext";
import Sidebar from "./UserPanel/Sidebar";

const EMPTY_FORM = {
  id: null,
  group: "",
  name: "",
  identification: "",
  email: "",
  phone: "",
  is_active: true,
};

function getApiMessage(data, fallback) {
  if (typeof data?.message === "string") {
    return data.message;
  }

  if (typeof data?.detail === "string") {
    return data.detail;
  }

  if (data?.errors && typeof data.errors === "object") {
    return Object.values(data.errors).flat().join(" ");
  }

  return fallback;
}

export default function AdminUsers() {
  const { fetchUser } = useAuth();

  const [organization, setOrganization] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [persons, setPersons] = useState([]);
  const [groups, setGroups] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [resettingId, setResettingId] = useState(null);

  const [form, setForm] = useState(EMPTY_FORM);
  const [formOpen, setFormOpen] = useState(false);
  const [detailPerson, setDetailPerson] = useState(null);
  const [deletePerson, setDeletePerson] = useState(null);
  const [credentials, setCredentials] = useState(null);

  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const [feedback, setFeedback] = useState({
    open: false,
    severity: "success",
    message: "",
  });

  const showFeedback = useCallback((severity, message) => {
    setFeedback({
      open: true,
      severity,
      message,
    });
  }, []);

  const readJson = async (response) => {
    try {
      return await response.json();
    } catch {
      return {};
    }
  };

  const loadData = useCallback(async () => {
    setLoading(true);

    try {
      const [organizationResponse, personsResponse, groupsResponse] =
        await Promise.all([
          fetchUser("/tenants/api/logo/", {
            method: "GET",
            cache: "no-store",
          }),
          fetchUser("/access-control/api/persons/", {
            method: "GET",
            cache: "no-store",
          }),
          fetchUser("/access-control/api/groups/", {
            method: "GET",
            cache: "no-store",
          }),
        ]);

      const organizationData = await readJson(organizationResponse);
      const personsData = await readJson(personsResponse);
      const groupsData = await readJson(groupsResponse);

      if (
        !organizationResponse.ok ||
        organizationData.ok !== true ||
        !organizationData.tenant
      ) {
        throw new Error(
          getApiMessage(
            organizationData,
            "No se pudo cargar la organización."
          )
        );
      }

      if (!personsResponse.ok || personsData.ok !== true) {
        throw new Error(
          getApiMessage(personsData, "No se pudieron cargar los usuarios.")
        );
      }

      if (!groupsResponse.ok || groupsData.ok !== true) {
        throw new Error(
          getApiMessage(groupsData, "No se pudieron cargar los roles.")
        );
      }

      setOrganization(organizationData.tenant);
      setPersons(Array.isArray(personsData.persons) ? personsData.persons : []);
      setGroups(Array.isArray(groupsData.groups) ? groupsData.groups : []);
    } catch (error) {
      showFeedback(
        "error",
        error instanceof Error
          ? error.message
          : "No se pudo conectar con el servidor."
      );
    } finally {
      setLoading(false);
    }
  }, [fetchUser, showFeedback]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const filteredPersons = useMemo(() => {
    const query = search.trim().toLowerCase();

    if (!query) {
      return persons;
    }

    return persons.filter((person) => {
      return (
        String(person.name || "").toLowerCase().includes(query) ||
        String(person.identification || "").toLowerCase().includes(query) ||
        String(person.email || "").toLowerCase().includes(query) ||
        String(person.phone || "").toLowerCase().includes(query) ||
        String(person.group_name || "").toLowerCase().includes(query)
      );
    });
  }, [persons, search]);

  const paginatedPersons = useMemo(() => {
    const start = page * rowsPerPage;
    return filteredPersons.slice(start, start + rowsPerPage);
  }, [filteredPersons, page, rowsPerPage]);

  useEffect(() => {
    const lastPage = Math.max(
      0,
      Math.ceil(filteredPersons.length / rowsPerPage) - 1
    );

    if (page > lastPage) {
      setPage(lastPage);
    }
  }, [filteredPersons.length, page, rowsPerPage]);

  const openCreate = () => {
    setForm(EMPTY_FORM);
    setFormOpen(true);
  };

  const openEdit = (person) => {
    setForm({
      id: person.id,
      group: person.group ?? "",
      name: person.name || "",
      identification: person.identification || "",
      email: person.email || "",
      phone: person.phone || "",
      is_active: person.is_active === true,
    });

    setFormOpen(true);
  };

  const closeForm = () => {
    if (saving) {
      return;
    }

    setFormOpen(false);
    setForm(EMPTY_FORM);
  };

  const handleFormChange = (event) => {
    const { name, value } = event.target;

    setForm((previous) => ({
      ...previous,
      [name]: value,
    }));
  };

  const handleSave = async () => {
    if (!form.name.trim()) {
      showFeedback("error", "El nombre es obligatorio.");
      return;
    }

    if (!form.identification.trim()) {
      showFeedback("error", "La identificación es obligatoria.");
      return;
    }

    if (!form.email.trim()) {
      showFeedback("error", "El correo es obligatorio.");
      return;
    }

    setSaving(true);

    try {
      const editing = Boolean(form.id);

      const response = await fetchUser(
        editing
          ? `/access-control/api/persons/${form.id}/`
          : "/access-control/api/persons/",
        {
          method: editing ? "PUT" : "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            group: form.group === "" ? null : Number(form.group),
            name: form.name.trim(),
            identification: form.identification.trim(),
            email: form.email.trim().toLowerCase(),
            phone: form.phone.trim(),
            is_active: form.is_active,
          }),
        }
      );

      const data = await readJson(response);

      if (!response.ok || data.ok !== true) {
        throw new Error(
          getApiMessage(
            data,
            editing
              ? "No se pudo actualizar el usuario."
              : "No se pudo crear el usuario."
          )
        );
      }

      setFormOpen(false);
      setForm(EMPTY_FORM);

      if (!editing && data.temporary_password) {
        setCredentials({
          temporaryPassword: data.temporary_password,
          whatsappMessage: data.whatsapp_message || "",
          phone: data.person?.phone || form.phone.trim(),
        });
      }

      showFeedback(
        "success",
        data.message ||
          (editing
            ? "Usuario actualizado correctamente."
            : "Usuario creado correctamente.")
      );

      await loadData();
    } catch (error) {
      showFeedback(
        "error",
        error instanceof Error
          ? error.message
          : "No se pudo guardar el usuario."
      );
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deletePerson) {
      return;
    }

    setDeletingId(deletePerson.id);

    try {
      const response = await fetchUser(
        `/access-control/api/persons/${deletePerson.id}/`,
        {
          method: "DELETE",
        }
      );

      const data = await readJson(response);

      if (!response.ok || data.ok === false) {
        throw new Error(
          getApiMessage(data, "No se pudo eliminar el usuario.")
        );
      }

      setDeletePerson(null);
      showFeedback(
        "success",
        data.message || "Usuario eliminado correctamente."
      );

      await loadData();
    } catch (error) {
      showFeedback(
        "error",
        error instanceof Error
          ? error.message
          : "No se pudo eliminar el usuario."
      );
    } finally {
      setDeletingId(null);
    }
  };

  const handleResetPassword = async (person) => {
    setResettingId(person.id);

    try {
      const response = await fetchUser(
        `/access-control/api/persons/${person.id}/reset-password/`,
        {
          method: "POST",
        }
      );

      const data = await readJson(response);

      if (!response.ok || data.ok !== true) {
        throw new Error(
          getApiMessage(data, "No se pudo restablecer la contraseña.")
        );
      }

      setCredentials({
        temporaryPassword: data.temporary_password || "",
        whatsappMessage: data.whatsapp_message || "",
        phone: person.phone || "",
      });

      showFeedback(
        "success",
        data.message || "Contraseña restablecida correctamente."
      );
    } catch (error) {
      showFeedback(
        "error",
        error instanceof Error
          ? error.message
          : "No se pudo restablecer la contraseña."
      );
    } finally {
      setResettingId(null);
    }
  };

  const openWhatsApp = () => {
    if (!credentials?.whatsappMessage) {
      return;
    }

    const phone = String(credentials.phone || "").replace(/\D/g, "");
    const message = encodeURIComponent(credentials.whatsappMessage);
    const url = phone
      ? `https://wa.me/${phone}?text=${message}`
      : `https://wa.me/?text=${message}`;

    window.open(url, "_blank", "noopener,noreferrer");
  };

  const formatDate = (value) => {
    if (!value) {
      return "Sin fecha";
    }

    const date = new Date(value);

    if (Number.isNaN(date.getTime())) {
      return "Sin fecha";
    }

    return new Intl.DateTimeFormat("es-EC", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
    }).format(date);
  };

  if (loading && !organization) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          backgroundColor: "#f5f5f5",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        minHeight: "100vh",
        display: "flex",
        backgroundColor: "#f5f5f5",
      }}
    >
      <Sidebar
        mobileOpen={mobileOpen}
        onClose={() => setMobileOpen(false)}
        organization={organization}
      />

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          minWidth: 0,
          padding: {
            xs: 2,
            sm: 3,
            md: 4,
          },
        }}
      >
        <Box
          sx={{
            width: "100%",
            maxWidth: 1200,
            margin: "0 auto",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: {
                xs: "flex-start",
                sm: "center",
              },
              justifyContent: "space-between",
              flexDirection: {
                xs: "column",
                sm: "row",
              },
              gap: 2,
              marginBottom: 3,
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                minWidth: 0,
                gap: 1,
              }}
            >
              <IconButton
                onClick={() => setMobileOpen(true)}
                sx={{
                  display: {
                    xs: "inline-flex",
                    md: "none",
                  },
                }}
              >
                <MenuRoundedIcon />
              </IconButton>

              <Box sx={{ minWidth: 0 }}>
                <Typography
                  component="h1"
                  sx={{
                    color: "#111111",
                    fontSize: {
                      xs: 26,
                      md: 32,
                    },
                    fontWeight: 800,
                    lineHeight: 1.2,
                  }}
                >
                  Usuarios
                </Typography>

                <Typography
                  sx={{
                    color: "#6b7280",
                    fontSize: 14,
                    fontWeight: 500,
                    marginTop: 0.5,
                  }}
                >
                  {persons.length} usuarios registrados
                </Typography>
              </Box>
            </Box>

            <Button
              variant="contained"
              onClick={openCreate}
              sx={{
                width: {
                  xs: "100%",
                  sm: "auto",
                },
                minHeight: 44,
                borderBottom: "3px solid #b00020",
              }}
            >
              Nuevo usuario
            </Button>
          </Box>

          <Paper
            elevation={0}
            sx={{
              width: "100%",
              border: "1px solid #e1e1e4",
              borderRadius: 3,
              overflow: "hidden",
            }}
          >
            <Box
              sx={{
                padding: {
                  xs: 2,
                  sm: 2.5,
                },
                borderBottom: "1px solid #e5e7eb",
              }}
            >
              <TextField
                fullWidth
                size="small"
                value={search}
                onChange={(event) => {
                  setSearch(event.target.value);
                  setPage(0);
                }}
                placeholder="Buscar por nombre, identificación, correo, teléfono o rol"
              />
            </Box>

            <Box
              sx={{
                display: {
                  xs: "none",
                  md: "block",
                },
              }}
            >
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell>Usuario</TableCell>
                      <TableCell>Identificación</TableCell>
                      <TableCell>Rol</TableCell>
                      <TableCell>Teléfono</TableCell>
                      <TableCell>Estado</TableCell>
                      <TableCell align="right">Acciones</TableCell>
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {paginatedPersons.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={6}>
                          <Box
                            sx={{
                              minHeight: 220,
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          >
                            <Typography sx={{ fontWeight: 800 }}>
                              No hay usuarios
                            </Typography>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ) : (
                      paginatedPersons.map((person) => (
                        <TableRow key={person.id} hover>
                          <TableCell>
                            <Typography
                              sx={{
                                fontSize: 14,
                                fontWeight: 800,
                              }}
                            >
                              {person.name}
                            </Typography>

                            <Typography
                              sx={{
                                color: "#6b7280",
                                fontSize: 12,
                                marginTop: 0.3,
                              }}
                            >
                              {person.email}
                            </Typography>
                          </TableCell>

                          <TableCell>{person.identification}</TableCell>
                          <TableCell>{person.group_name || "Sin rol"}</TableCell>
                          <TableCell>{person.phone || "Sin teléfono"}</TableCell>

                          <TableCell>
                            <Chip
                              size="small"
                              label={person.is_active ? "Activo" : "Inactivo"}
                              sx={{
                                fontWeight: 700,
                                color: person.is_active
                                  ? "#047857"
                                  : "#b00020",
                                backgroundColor: person.is_active
                                  ? "#ecfdf5"
                                  : "#fff1f2",
                              }}
                            />
                          </TableCell>

                          <TableCell align="right">
                            <Stack
                              direction="row"
                              spacing={1}
                              justifyContent="flex-end"
                              flexWrap="wrap"
                              useFlexGap
                            >
                              <Button
                                size="small"
                                variant="outlined"
                                onClick={() => setDetailPerson(person)}
                              >
                                Ver
                              </Button>

                              <Button
                                size="small"
                                variant="outlined"
                                onClick={() => openEdit(person)}
                              >
                                Editar
                              </Button>

                              <Button
                                size="small"
                                variant="outlined"
                                disabled={resettingId === person.id}
                                onClick={() => handleResetPassword(person)}
                              >
                                {resettingId === person.id
                                  ? "Procesando"
                                  : "Restablecer clave"}
                              </Button>

                              <Button
                                size="small"
                                variant="outlined"
                                color="error"
                                onClick={() => setDeletePerson(person)}
                              >
                                Eliminar
                              </Button>
                            </Stack>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Box>

            <Box
              sx={{
                display: {
                  xs: "block",
                  md: "none",
                },
                padding: 2,
              }}
            >
              {paginatedPersons.length === 0 ? (
                <Box
                  sx={{
                    minHeight: 180,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Typography sx={{ fontWeight: 800 }}>
                    No hay usuarios
                  </Typography>
                </Box>
              ) : (
                <Stack spacing={1.5}>
                  {paginatedPersons.map((person) => (
                    <Box
                      key={person.id}
                      sx={{
                        padding: 2,
                        border: "1px solid #e5e7eb",
                        borderRadius: 2,
                        backgroundColor: "#ffffff",
                      }}
                    >
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "flex-start",
                          justifyContent: "space-between",
                          gap: 1,
                          marginBottom: 1.5,
                        }}
                      >
                        <Box sx={{ minWidth: 0 }}>
                          <Typography
                            sx={{
                              fontSize: 16,
                              fontWeight: 800,
                              wordBreak: "break-word",
                            }}
                          >
                            {person.name}
                          </Typography>

                          <Typography
                            sx={{
                              color: "#6b7280",
                              fontSize: 13,
                              wordBreak: "break-word",
                            }}
                          >
                            {person.email}
                          </Typography>
                        </Box>

                        <Chip
                          size="small"
                          label={person.is_active ? "Activo" : "Inactivo"}
                          sx={{
                            flexShrink: 0,
                            fontWeight: 700,
                            color: person.is_active ? "#047857" : "#b00020",
                            backgroundColor: person.is_active
                              ? "#ecfdf5"
                              : "#fff1f2",
                          }}
                        />
                      </Box>

                      <Typography sx={{ fontSize: 13, marginBottom: 0.5 }}>
                        <strong>Identificación:</strong>{" "}
                        {person.identification}
                      </Typography>

                      <Typography sx={{ fontSize: 13, marginBottom: 0.5 }}>
                        <strong>Rol:</strong>{" "}
                        {person.group_name || "Sin rol"}
                      </Typography>

                      <Typography sx={{ fontSize: 13, marginBottom: 1.5 }}>
                        <strong>Teléfono:</strong>{" "}
                        {person.phone || "Sin teléfono"}
                      </Typography>

                      <Box
                        sx={{
                          display: "grid",
                          gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
                          gap: 1,
                        }}
                      >
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => setDetailPerson(person)}
                        >
                          Ver
                        </Button>

                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => openEdit(person)}
                        >
                          Editar
                        </Button>

                        <Button
                          size="small"
                          variant="outlined"
                          disabled={resettingId === person.id}
                          onClick={() => handleResetPassword(person)}
                          sx={{ gridColumn: "1 / -1" }}
                        >
                          {resettingId === person.id
                            ? "Procesando"
                            : "Restablecer clave"}
                        </Button>

                        <Button
                          size="small"
                          variant="outlined"
                          color="error"
                          onClick={() => setDeletePerson(person)}
                          sx={{ gridColumn: "1 / -1" }}
                        >
                          Eliminar
                        </Button>
                      </Box>
                    </Box>
                  ))}
                </Stack>
              )}
            </Box>

            <TablePagination
              component="div"
              count={filteredPersons.length}
              page={page}
              onPageChange={(_, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(event) => {
                setRowsPerPage(Number(event.target.value));
                setPage(0);
              }}
              rowsPerPageOptions={[5, 10, 25]}
              labelRowsPerPage="Filas"
              sx={{
                overflow: "hidden",
                "& .MuiTablePagination-toolbar": {
                  flexWrap: "wrap",
                  justifyContent: "flex-end",
                },
              }}
            />
          </Paper>
        </Box>
      </Box>

      <Dialog
        open={formOpen}
        onClose={closeForm}
        fullWidth
        maxWidth="sm"
        fullScreen={false}
      >
        <DialogTitle>
          {form.id ? "Editar usuario" : "Nuevo usuario"}
        </DialogTitle>

        <DialogContent>
          <Stack spacing={2} sx={{ paddingTop: 1 }}>
            <FormControl fullWidth>
              <InputLabel id="user-group-label">Rol</InputLabel>

              <Select
                labelId="user-group-label"
                name="group"
                value={form.group}
                label="Rol"
                onChange={handleFormChange}
              >
                <MenuItem value="">Sin rol</MenuItem>

                {groups
                  .filter((group) => group.is_active || group.id === form.group)
                  .map((group) => (
                    <MenuItem key={group.id} value={group.id}>
                      {group.name}
                    </MenuItem>
                  ))}
              </Select>
            </FormControl>

            <TextField
              fullWidth
              required
              name="name"
              label="Nombre"
              value={form.name}
              onChange={handleFormChange}
            />

            <TextField
              fullWidth
              required
              name="identification"
              label="Identificación"
              value={form.identification}
              onChange={handleFormChange}
            />

            <TextField
              fullWidth
              required
              name="email"
              type="email"
              label="Correo"
              value={form.email}
              onChange={handleFormChange}
            />

            <TextField
              fullWidth
              name="phone"
              label="Teléfono"
              value={form.phone}
              onChange={handleFormChange}
            />

            {form.id && (
              <Box
                sx={{
                  minHeight: 48,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  border: "1px solid #e5e7eb",
                  borderRadius: 2,
                  paddingX: 1.5,
                }}
              >
                <Typography sx={{ fontSize: 14, fontWeight: 700 }}>
                  Usuario activo
                </Typography>

                <Switch
                  checked={form.is_active}
                  onChange={(event) =>
                    setForm((previous) => ({
                      ...previous,
                      is_active: event.target.checked,
                    }))
                  }
                />
              </Box>
            )}
          </Stack>
        </DialogContent>

        <DialogActions>
          <Button onClick={closeForm} disabled={saving}>
            Cancelar
          </Button>

          <Button
            variant="contained"
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? (
              <CircularProgress size={20} sx={{ color: "#ffffff" }} />
            ) : form.id ? (
              "Guardar cambios"
            ) : (
              "Crear usuario"
            )}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(detailPerson)}
        onClose={() => setDetailPerson(null)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Información del usuario</DialogTitle>

        <DialogContent>
          {detailPerson && (
            <Stack spacing={1.5} sx={{ paddingTop: 1 }}>
              {[
                ["Nombre", detailPerson.name],
                ["Identificación", detailPerson.identification],
                ["Correo", detailPerson.email],
                ["Teléfono", detailPerson.phone || "Sin teléfono"],
                ["Rol", detailPerson.group_name || "Sin rol"],
                ["Estado", detailPerson.is_active ? "Activo" : "Inactivo"],
                ["Fecha de creación", formatDate(detailPerson.created_at)],
                [
                  "Última actualización",
                  formatDate(detailPerson.updated_at),
                ],
              ].map(([label, value]) => (
                <Box
                  key={label}
                  sx={{
                    paddingBottom: 1.2,
                    borderBottom: "1px solid #e5e7eb",
                  }}
                >
                  <Typography
                    sx={{
                      color: "#6b7280",
                      fontSize: 12,
                      fontWeight: 700,
                    }}
                  >
                    {label}
                  </Typography>

                  <Typography
                    sx={{
                      fontSize: 15,
                      fontWeight: 700,
                      marginTop: 0.3,
                      wordBreak: "break-word",
                    }}
                  >
                    {value}
                  </Typography>
                </Box>
              ))}
            </Stack>
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setDetailPerson(null)}>Cerrar</Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(deletePerson)}
        onClose={() => {
          if (!deletingId) {
            setDeletePerson(null);
          }
        }}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Eliminar usuario</DialogTitle>

        <DialogContent>
          <Typography>
            ¿Deseas eliminar a {deletePerson?.name}?
          </Typography>
        </DialogContent>

        <DialogActions>
          <Button
            onClick={() => setDeletePerson(null)}
            disabled={Boolean(deletingId)}
          >
            Cancelar
          </Button>

          <Button
            variant="contained"
            color="error"
            onClick={handleDelete}
            disabled={Boolean(deletingId)}
          >
            {deletingId ? (
              <CircularProgress size={20} sx={{ color: "#ffffff" }} />
            ) : (
              "Eliminar"
            )}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(credentials)}
        onClose={() => setCredentials(null)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>Credenciales temporales</DialogTitle>

        <DialogContent>
          <Box
            sx={{
              minHeight: 70,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              backgroundColor: "#000000",
              borderBottom: "4px solid #b00020",
              borderRadius: 2,
              marginTop: 1,
              marginBottom: 2,
            }}
          >
            <Typography
              sx={{
                color: "#ffffff",
                fontSize: {
                  xs: 23,
                  sm: 28,
                },
                fontWeight: 800,
                letterSpacing: 4,
              }}
            >
              {credentials?.temporaryPassword || "No disponible"}
            </Typography>
          </Box>

          {credentials?.whatsappMessage && (
            <TextField
              fullWidth
              multiline
              minRows={4}
              value={credentials.whatsappMessage}
              InputProps={{
                readOnly: true,
              }}
            />
          )}
        </DialogContent>

        <DialogActions>
          <Button onClick={() => setCredentials(null)}>Cerrar</Button>

          {credentials?.whatsappMessage && (
            <Button variant="contained" onClick={openWhatsApp}>
              Enviar por WhatsApp
            </Button>
          )}
        </DialogActions>
      </Dialog>

      <Snackbar
        open={feedback.open}
        autoHideDuration={4000}
        onClose={() =>
          setFeedback((previous) => ({
            ...previous,
            open: false,
          }))
        }
        anchorOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        <Alert
          severity={feedback.severity}
          variant="filled"
          onClose={() =>
            setFeedback((previous) => ({
              ...previous,
              open: false,
            }))
          }
        >
          {feedback.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}